
export const calculateColumnWidths = (columns = [], showActions = true, actionsWidth = "10%") => {
	const totalColumns = columns.length + (showActions ? 1 : 0);

	// Separar columnas con ancho definido y sin definir
	const columnsWithWidth = columns.filter(col => col.width);
	const columnsWithoutWidth = columns.filter(col => !col.width);

	// Convertir anchos a números para cálculos
	const parseWidth = (width) => {
		if (typeof width === 'string' && width.includes('%')) {
			return parseFloat(width.replace('%', ''));
		}
		if (typeof width === 'number') {
			return width;
		}
		return 0;
	};

	// Calcular espacio usado por columnas con ancho definido
	const usedSpace = columnsWithWidth.reduce((total, col) => {
		return total + parseWidth(col.width);
	}, 0);

	// Agregar espacio de la columna de acciones si está habilitada
	const actionsSpace = showActions ? parseWidth(actionsWidth) : 0;
	const totalUsedSpace = usedSpace + actionsSpace;

	// Calcular espacio disponible para columnas sin ancho
	const availableSpace = Math.max(0, 100 - totalUsedSpace);

	// Calcular ancho automático para cada columna sin ancho definido
	const autoWidth = columnsWithoutWidth.length > 0
		? availableSpace / columnsWithoutWidth.length
		: 0;

	// Crear objeto con anchos finales
	const calculatedWidths = {};

	// Asignar anchos a todas las columnas
	columns.forEach(column => {
		if (column.width) {
			// Mantener ancho definido manualmente
			calculatedWidths[column.field] = column.width;
		} else {
			// Asignar ancho automático calculado
			calculatedWidths[column.field] = columnsWithoutWidth.length > 0
				? `${autoWidth.toFixed(2)}%`
				: `${(100 / totalColumns).toFixed(2)}%`;
		}
	});

	// Agregar ancho de acciones si está habilitada
	if (showActions) {
		calculatedWidths.actions = actionsWidth;
	}

	// Calcular totales para el estilo final
	let totalPercent = 0;
	let totalPixels = 0;

	const addToTotal = (w) => {
		if (typeof w === 'number') {
			totalPixels += w;
		} else if (typeof w === 'string') {
			if (w.includes('%')) {
				totalPercent += parseFloat(w);
			} else {
				totalPixels += parseFloat(w) || 0;
			}
		}
	};

	// Sumar anchos de columnas definidas
	columnsWithWidth.forEach(col => addToTotal(col.width));

	// Sumar anchos de columnas automáticas
	if (columnsWithoutWidth.length > 0) {
		totalPercent += columnsWithoutWidth.length * autoWidth;
	}

	// Sumar ancho de acciones
	if (showActions) {
		addToTotal(actionsWidth);
	}

	// Check normalization requirement for pure percentage overflow
	if (totalPercent > 100 && totalPixels === 0) {
		const scaleFactor = totalPercent / 100;

		// The container will be set to this width (e.g. 126%) relative to viewport
		const normalizedTotalWidthStyle = `${totalPercent}%`;

		// Rescale all percentage widths so they sum to 100% RELATIVE TO the new container
		// NewWidth% = OriginalWidth% / ScaleFactor
		// e.g. 18% / 1.26 = 14.28%
		// 14.28% of 126% container = 18% of viewport. Correct.

		const normalizedWidths = { ...calculatedWidths };

		columns.forEach(column => {
			if (column.width && typeof column.width === 'string' && column.width.includes('%')) {
				const val = parseFloat(column.width);
				normalizedWidths[column.field] = `${(val / scaleFactor).toFixed(4)}%`;
			}
		});

		if (showActions && typeof actionsWidth === 'string' && actionsWidth.includes('%')) {
			const val = parseFloat(actionsWidth);
			normalizedWidths.actions = `${(val / scaleFactor).toFixed(4)}%`;
		}

		return {
			widths: normalizedWidths,
			info: {
				totalColumns,
				columnsWithManualWidth: columnsWithWidth.length,
				columnsWithAutoWidth: columnsWithoutWidth.length,
				usedSpace: totalUsedSpace,
				availableSpace,
				autoWidthPerColumn: autoWidth,
				totalWidthStyle: normalizedTotalWidthStyle,
			}
		};
	}

	const totalWidthStyle = totalPixels > 0
		? `calc(${totalPercent}% + ${totalPixels}px)`
		: `${totalPercent}%`;

	return {
		widths: calculatedWidths,
		info: {
			totalColumns,
			columnsWithManualWidth: columnsWithWidth.length,
			columnsWithAutoWidth: columnsWithoutWidth.length,
			usedSpace: totalUsedSpace,
			availableSpace,
			autoWidthPerColumn: autoWidth,
			totalWidthStyle,
		}
	};
};

/**
 * Aplica los anchos calculados a las columnas
 * Modifica el array de columnas agregando la propiedad calculatedWidth
 * 
 * @param {Array} columns - Array de columnas
 * @param {boolean} showActions - Si se muestra la columna de acciones
 * @param {string} actionsWidth - Ancho de la columna de acciones
 * @returns {Array} - Array de columnas con anchos calculados
 */
export const applyCalculatedWidths = (columns, showActions = true, actionsWidth = "10%") => {
	const { widths } = calculateColumnWidths(columns, showActions, actionsWidth);

	return columns.map(column => ({
		...column,
		calculatedWidth: widths[column.field],
		// Mantener el ancho original como fallback
		width: widths[column.field]
	}));
};

/**
 * Hook para usar anchos calculados en componentes React
 * 
 * @param {Array} columns - Array de columnas
 * @param {boolean} showActions - Si se muestra la columna de acciones
 * @param {string} actionsWidth - Ancho de la columna de acciones
 * @returns {Object} - Objeto con columnas procesadas y información de anchos
 */
export const useCalculatedWidths = (columns, showActions = true, actionsWidth = "10%") => {
	const result = calculateColumnWidths(columns, showActions, actionsWidth);
	const processedColumns = applyCalculatedWidths(columns, showActions, actionsWidth);

	return {
		columns: processedColumns,
		widths: result.widths,
		info: result.info,
		actionsWidth: result.widths.actions || actionsWidth
	};
};
